import { useState } from 'react';
import Modal from './Modal';
import { useToast } from '../context/ToastContext';

// Field-driven create/edit modal.
// fields: [{ name, label, type?, required?, options?, placeholder?, help? }]
//   type: text(default)|number|textarea|select|checkbox|date|email|password
// useCreate/useUpdate: hook factories from makeCrudHooks.
export default function ResourceForm({ title, open, record, fields, useCreate, useUpdate, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = !!record;
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [seededFor, setSeededFor] = useState(null);

  // Seed defaults from record (or blanks) whenever the modal (re)opens for a new target.
  if (open && seededFor !== (record?.id ?? 'new')) {
    const seed = {};
    fields.forEach((f) => {
      const v = record ? record[f.name] : undefined;
      seed[f.name] = v ?? (f.type === 'checkbox' ? false : f.type === 'number' ? 0 : '');
    });
    setForm(seed);
    setErrors({});
    setSeededFor(record?.id ?? 'new');
  }
  if (!open && seededFor !== null) setSeededFor(null);

  const done = () => { toast.success(isEdit ? 'Updated' : 'Created'); onSaved(); };
  const fail = (e) => toast.error(e.message || 'Save failed');
  const createM = useCreate({ onSuccess: done, onError: fail });
  const updateM = useUpdate({ onSuccess: done, onError: fail });
  const pending = createM.isPending || updateM.isPending;

  const validate = () => {
    const er = {};
    fields.forEach((f) => {
      const v = form[f.name];
      if (f.required && (v === '' || v == null)) er[f.name] = `${f.label} is required`;
      if (f.type === 'number' && v !== '' && v != null && isNaN(Number(v))) er[f.name] = 'Must be a number';
    });
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const body = {};
    fields.forEach((f) => {
      let v = form[f.name];
      if (f.type === 'number') v = v === '' ? null : Number(v);
      if (typeof v === 'string') v = v.trim();
      body[f.name] = v;
    });
    if (isEdit) updateM.mutate({ id: record.id, body });
    else createM.mutate(body);
  };

  const setVal = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        {fields.map((f) => (
          <label className="field" key={f.name}>
            <span>{f.label}{f.required && ' *'}</span>
            {f.type === 'textarea' ? (
              <textarea rows={4} value={form[f.name] ?? ''} onChange={(e) => setVal(f.name, e.target.value)} placeholder={f.placeholder} />
            ) : f.type === 'select' ? (
              <select value={form[f.name] ?? ''} onChange={(e) => setVal(f.name, e.target.value)}>
                <option value="">— select —</option>
                {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
              </select>
            ) : f.type === 'checkbox' ? (
              <input type="checkbox" checked={!!form[f.name]} onChange={(e) => setVal(f.name, e.target.checked)} />
            ) : (
              <input type={f.type || 'text'} value={form[f.name] ?? ''} onChange={(e) => setVal(f.name, e.target.value)} placeholder={f.placeholder} />
            )}
            {f.help && <em className="field__help">{f.help}</em>}
            {errors[f.name] && <em className="field__error">{errors[f.name]}</em>}
          </label>
        ))}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={pending}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
